import { Observable } from 'rxjs';
import { observable, computed, reaction, action, createAtom } from 'mobx';

/**
 * Creates an RxJS Observable stream from the specified `expression` which
 * refers to MobX observables.
 *
 * @param expression
 * @param fireImmediately
 */
export function toStream(expression) {
  var fireImmediately = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  return new Observable(function (observer) {
    var computedValue = computed(expression);
    return reaction(function () {
      return computedValue.get();
    }, function (newValue) {
      return observer.next(newValue);
    }, {
      fireImmediately: fireImmediately
    });
  });
}

/**
 * Returns a getter function for the value of an observable that will
 * be updated any time the Rx stream emits.
 *
 * NOTE: The `stream` should complete when no longer used. Alternatively,
 * call the `dispose` method on the returned function, OR use `asAtom`.
 * This is because **the stream is KEPT ALIVE** otherwise.
 *
 * @param initialValue The initial value to emit on subscribe
 * @param stream The stream to observe.
 */
export function fromStream(initialValue, stream) {
  var box = observable.box(initialValue);
  var sub = stream.subscribe(action(function (value) {
    return box.set(value);
  }));
  function get() {
    return box.get();
  }
  get.dispose = function () {
    sub.unsubscribe();
  };
  return get;
}

/**
 * An alternative to `fromStream`, will manage the subscription
 * based on MobX observability status.
 *
 * When the returned atom becomes observed, subscribes to the stream.
 * When it becomes unobserved, unsubscribes.
 */

export function asAtom(stream) {
  var initialValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var subscription = null;
  var value = initialValue;
  var atom = createAtom('RxStream', function () {
    subscription = stream.subscribe({
      next: function next(newValue) {
        value = newValue;
        atom.reportChanged();
      }
    });
  }, function () {
    subscription && subscription.unsubscribe();
    subscription = null;
  });
  return function () {
    atom.reportObserved();
    return value;
  };
}
//# sourceMappingURL=mobxUtil.js.map
